import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Contact.css';

const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const contactInfo = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      label: 'Email',
      value: 'banerjeearjun73@gmail.com',
      link: 'mailto:banerjeearjun73@gmail.com',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      ),
      label: 'Phone',
      value: '510-304-8514',
      link: 'tel:510-304-8514',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
          <rect x="2" y="9" width="4" height="12"/>
          <circle cx="4" cy="4" r="2"/>
        </svg>
      ),
      label: 'LinkedIn',
      value: 'linkedin.com/in/arjunbanerjee77',
      link: 'https://www.linkedin.com/in/arjunbanerjee77/',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      ),
      label: 'Location',
      value: 'Fremont, CA (Open to Relocation)',
      link: null,
    },
  ];

  const targetCities = ['San Francisco', 'Boston', 'San Jose', 'New York'];

  const testimonials = [
    {
      name: 'Saibal Banerjee, Ph.D.',
      title: 'Generative-AI Privacy CTO',
      relationship: 'Managed Arjun directly',
      quote: 'Arjun is an extraordinary out-of-the-box independent thinker. He collaborated with me on developing Computational Differential Privacy—a completely new subfield that combines standard Differential Privacy with Machine Learning approaches. He is an extremely fast learner, as evidenced by how quickly he pivoted to an unfamiliar subject and developed the SaaS software for tomtA.',
    },
    {
      name: 'Erik Dreispiel',
      title: 'Data Engineer',
      relationship: 'Worked on same team',
      quote: 'Arjun is an extremely talented and experienced technical co-founder. He demonstrated mastery in Image Processing with C++ and working with Raspberry Pi Zero devices. He was an apt entrepreneur who not only worked in the Purdue ecosystem but also looped in teams from large automotive companies like Continental.',
    },
  ];

  return (
    <section id="contact" className="contact section" ref={ref}>
      <div className="contact-background">
        <div className="contact-gradient"></div>
        <div className="contact-pattern"></div>
      </div>

      <div className="container">
        <motion.div
          className="section-title contact-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Let's Connect</h2>
          <p className="section-subtitle">
            Open to opportunities where you would need an AI Product Architect
          </p>
        </motion.div>

        <div className="contact-grid">
          {/* Contact Info */}
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="contact-intro">
              <h3>Ready for New Challenges</h3>
              <p>
                Seeking <strong>Consulting work in GenAI/Machine Learning Product Development and Design</strong> or 
                <strong>Full Time work as a Machine Learning Scientist/Data Scientist/Product Manager</strong> where 
                I can leverage my 10+ years of experience designing and building enterprise AI/ML solutions.
              </p>
            </div>

            <div className="contact-details">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  className="contact-item"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <div className="contact-icon">
                    {item.icon}
                  </div>
                  <div className="contact-text">
                    <span className="contact-label">{item.label}</span>
                    {item.link ? (
                      <a href={item.link} className="contact-value" target={item.link.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
                        {item.value}
                      </a>
                    ) : (
                      <span className="contact-value">{item.value}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="relocation-info">
              <h4>Target Locations</h4>
              <div className="city-tags">
                {targetCities.map((city, index) => (
                  <span key={index} className="city-tag">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Hiring Info Card */}
          <motion.div
            className="hiring-card"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="hiring-badge">
              <span className="status-dot"></span>
              Available for Opportunities
            </div>

            <h3>What I Bring</h3>
            
            <ul className="value-props">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>10+ years ML/AI experience</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>5 successful enterprise POCs delivered</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>3 patents in GenAI and Machine Learning Algorithms</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Healthcare, Finance, Telecom domain knowledge</span>
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>CMU MS + Purdue BS credentials</span>
              </li>
            </ul>

            <div className="hiring-cta">
              <a href="mailto:banerjeearjun73@gmail.com" className="btn btn-primary btn-full">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                Send Email
              </a>
              <a href="https://www.linkedin.com/in/arjunbanerjee77/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
                Connect on LinkedIn
              </a>
            </div>
          </motion.div>
        </div>

        {/* Testimonials */}
        <motion.div
          className="testimonials-section"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3>What Colleagues Say</h3>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-quote">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="quote-icon">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                  </svg>
                  <p>{testimonial.quote}</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-info">
                    <span className="author-name">{testimonial.name}</span>
                    <span className="author-title">{testimonial.title}</span>
                    <span className="author-relationship">{testimonial.relationship}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;

